import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDatasetError } from '../../../api/types/types';

type UseFormPollErrorGuardParams = {
  // Master dataset rows (undefined until the first load succeeds).
  data: any[] | undefined;
  datasetId: string | undefined;
  // Latest row-data error from the dataset data hook.
  dataError: useDatasetError;
  // Drops only the error (no epoch bump / loading change).
  clearDataError: () => void;
};

// Guards the form against a long-running data poll that errors out underneath it.
//
// Scenario: the user opens a dataset whose data load is slow (polling starts),
// then opens a form BEFORE the data arrives, and the still-running data request
// fails. The form must NOT be covered by that row-data error — the form is
// independent of the list rows. On leaving the form the list re-fetches the data
// on its own (filterFirst return guard / list mount effect), so the error is
// dropped here rather than re-surfacing on the list.
//
// The guard fires ONLY in that exact case — on a /form/ route with no data yet
// (data === undefined). Every other data error (on the list, with data already
// present, or a definition/filter error) is left untouched and behaves as before.
//
// Returns whether the apiError selection should suppress the current dataError.
export const useFormPollErrorGuard = ({
  data,
  datasetId,
  dataError,
  clearDataError,
}: UseFormPollErrorGuardParams): boolean => {
  const { pathname } = useLocation();

  const shouldSuppressDataError =
    pathname.includes('/form/') && data === undefined;

  useEffect(() => {
    if (shouldSuppressDataError && dataError?.datasetId === datasetId) {
      clearDataError();
    }
  }, [shouldSuppressDataError, dataError, datasetId, clearDataError]);

  return shouldSuppressDataError;
};
